import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import BreadCrumb from '../BreadCrumb';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
  DocumentTextIcon,
  BanknotesIcon,
  CreditCardIcon,
  CalculatorIcon,
  ClipboardDocumentListIcon,
  PrinterIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckBadgeIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

const AccountsGuide = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [activeTab, setActiveTab] = useState('quotations');

  const pages = [
    { name: 'Help Center', to: '/help', component: Link, current: false },
    { name: 'Accounts Quick Guide', to: '/help/accounts-guide', component: 'span', current: true },
  ];

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const getColorClasses = (color) => {
    const colors = {
      blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-800', icon: 'text-blue-600' },
      purple: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', badge: 'bg-purple-100 text-purple-800', icon: 'text-purple-600' },
      green: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', badge: 'bg-green-100 text-green-800', icon: 'text-green-600' },
      yellow: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', badge: 'bg-yellow-100 text-yellow-800', icon: 'text-yellow-600' },
      orange: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', badge: 'bg-orange-100 text-orange-800', icon: 'text-orange-600' },
      red: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', badge: 'bg-red-100 text-red-800', icon: 'text-red-600' },
      gray: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700', badge: 'bg-gray-100 text-gray-800', icon: 'text-gray-600' },
      indigo: { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700', badge: 'bg-indigo-100 text-indigo-800', icon: 'text-indigo-600' },
    };
    return colors[color] || colors.gray;
  };

  const faqs = [
    {
      category: 'Quotation Questions',
      questions: [
        {
          q: 'Can I edit a quotation after it has been approved?',
          a: 'No. Once a quotation is approved, it cannot be edited as an invoice has been generated from it. If changes are needed, you must create a new quotation for the job card.'
        },
        {
          q: 'Why is my quotation not saving?',
          a: 'Check that: (1) A job card is selected, (2) At least one item has been added, (3) All required fields are filled. Also ensure you have a stable internet connection.'
        },
        {
          q: 'How do I add tax to a quotation?',
          a: 'Check the "Include Tax" checkbox before saving. You can also select "Tax on Services Only" to apply 7.5% VAT only to service items, not parts.'
        },
        {
          q: 'Can I create multiple quotations for the same job card?',
          a: 'Yes, but the system will warn you if a quotation already exists for that job card. This helps prevent duplicate quotations.'
        }
      ]
    },
    {
      category: 'Invoice Questions',
      questions: [
        {
          q: 'What is the difference between creating an invoice from quotation vs manually?',
          a: 'From Quotation (Recommended): Auto-populates all details, maintains audit trail, links to job card. Manual: For standalone invoices or supplementary bills not linked to job cards.'
        },
        {
          q: 'Why is my invoice showing "Pending" after I recorded a payment?',
          a: 'If the payment amount is less than the total receivable, the status changes to "Part Payment". It only becomes "Fully Paid" when receivable reaches zero.'
        },
        {
          q: 'Can I delete an invoice?',
          a: 'Invoices should not be deleted to maintain proper accounting records. If an invoice was created in error, contact your administrator.'
        }
      ]
    },
    {
      category: 'Payment Questions',
      questions: [
        {
          q: 'What payment modes are available?',
          a: 'Bank Transfer (Stanbic Opex, Revenue, or CAPEX accounts), Cash, and POS. Select the appropriate mode based on how the customer paid.'
        },
        {
          q: 'Can I backdate a payment?',
          a: 'You can set a past date, but it cannot be earlier than the invoice approval date. The system validates this automatically.'
        },
        {
          q: 'What if I entered the wrong payment amount?',
          a: 'Contact your administrator to correct payment records. Always double-check amounts before submitting.'
        },
        {
          q: 'How do I record a refund?',
          a: 'Refunds should be processed through the administrator. The system currently tracks incoming payments, not outgoing refunds.'
        }
      ]
    },
    {
      category: 'Receivables & Payables Questions',
      questions: [
        {
          q: 'How do I follow up on aged receivables?',
          a: 'Go to Finance > Receivables to see all outstanding invoices. Sort by date to identify old debts. The aging categories are: Current, 30+ days, 60+ days, 90+ days.'
        },
        {
          q: 'What happens when I mark a payable as paid?',
          a: 'The payable status updates, an expense transaction is created, and the balance is adjusted. Make sure to record the actual payment date.'
        },
        {
          q: 'Can I partially pay a vendor bill?',
          a: 'Yes! Enter the partial amount, and the status will change to "Partially Paid". The remaining balance stays in the payables list.'
        }
      ]
    }
  ];

  const statusReference = {
    invoice: [
      { status: 'Pending', meaning: 'Invoice created, awaiting payment', color: 'yellow', action: 'Follow up with customer' },
      { status: 'Part Payment', meaning: 'Partial payment received', color: 'blue', action: 'Collect remaining balance' },
      { status: 'Fully Paid', meaning: 'Complete payment received', color: 'green', action: 'No action needed' },
    ],
    quotation: [
      { status: 'Pending', meaning: 'Awaiting approval', color: 'yellow', action: 'Review and approve or edit' },
      { status: 'Approved', meaning: 'Approved, invoice generated', color: 'green', action: 'No action needed' },
    ],
    payable: [
      { status: 'Unpaid', meaning: 'No payment made', color: 'red', action: 'Process payment' },
      { status: 'Partially Paid', meaning: 'Partial payment made', color: 'yellow', action: 'Complete payment' },
      { status: 'Paid', meaning: 'Fully paid', color: 'green', action: 'No action needed' },
    ]
  };

  const quickActions = [
    { name: 'Create Quotation', path: '/finance/quotations', icon: DocumentTextIcon, color: 'blue', description: 'Start a new quotation from job card' },
    { name: 'View Invoices', path: '/finance/invoices', icon: ClipboardDocumentListIcon, color: 'purple', description: 'Manage all invoices' },
    { name: 'Receivables', path: '/finance/receivables', icon: BanknotesIcon, color: 'green', description: 'Track customer balances' },
    { name: 'Payables', path: '/finance/payables', icon: CreditCardIcon, color: 'orange', description: 'Manage vendor payments' },
  ];

  return (
    <>
      <BreadCrumb pages={pages} />

      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-lg shadow-lg p-8 mb-8 text-white">
          <div className="flex items-center mb-4">
            <BanknotesIcon className="h-12 w-12 mr-4" />
            <div>
              <h1 className="text-4xl font-bold">Accounts Quick Guide</h1>
              <p className="text-xl text-yellow-100">Finance Module Reference for AYDENS Garage</p>
            </div>
          </div>
          <p className="text-yellow-100 mt-2">
            Complete guide for managing quotations, invoices, payments, receivables, and payables.
          </p>
          <div className="mt-4 flex items-center text-yellow-200 text-sm">
            <ClockIcon className="h-4 w-4 mr-1" />
            Last Updated: January 2026
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {quickActions.map((action) => {
              const Icon = action.icon;
              const colors = getColorClasses(action.color);
              return (
                <Link
                  key={action.name}
                  to={action.path}
                  className={`flex items-center p-4 ${colors.bg} hover:opacity-80 rounded-lg border-2 ${colors.border} transition-all hover:shadow-md`}
                >
                  <Icon className={`h-10 w-10 ${colors.icon} mr-3`} />
                  <div>
                    <h3 className={`font-semibold ${colors.text}`}>{action.name}</h3>
                    <p className="text-sm text-gray-600">{action.description}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Document Flow Overview */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <ArrowPathIcon className="h-7 w-7 text-blue-600 mr-2" />
            Document Flow Overview
          </h2>

          <div className="bg-gray-50 rounded-lg p-6 border-2 border-gray-200">
            <div className="flex flex-wrap items-center justify-center gap-2 md:gap-4">
              <div className="flex-shrink-0 bg-blue-100 rounded-lg px-4 py-3 text-center border-2 border-blue-300">
                <DocumentTextIcon className="h-8 w-8 text-blue-600 mx-auto mb-1" />
                <span className="text-sm font-medium text-blue-900">Job Card</span>
              </div>
              <span className="text-gray-400 text-2xl">→</span>
              <div className="flex-shrink-0 bg-purple-100 rounded-lg px-4 py-3 text-center border-2 border-purple-300">
                <CalculatorIcon className="h-8 w-8 text-purple-600 mx-auto mb-1" />
                <span className="text-sm font-medium text-purple-900">Quotation</span>
              </div>
              <span className="text-gray-400 text-2xl">→</span>
              <div className="flex-shrink-0 bg-yellow-100 rounded-lg px-4 py-3 text-center border-2 border-yellow-300">
                <CheckBadgeIcon className="h-8 w-8 text-yellow-600 mx-auto mb-1" />
                <span className="text-sm font-medium text-yellow-900">Approval</span>
              </div>
              <span className="text-gray-400 text-2xl">→</span>
              <div className="flex-shrink-0 bg-indigo-100 rounded-lg px-4 py-3 text-center border-2 border-indigo-300">
                <ClipboardDocumentListIcon className="h-8 w-8 text-indigo-600 mx-auto mb-1" />
                <span className="text-sm font-medium text-indigo-900">Invoice</span>
              </div>
              <span className="text-gray-400 text-2xl">→</span>
              <div className="flex-shrink-0 bg-green-100 rounded-lg px-4 py-3 text-center border-2 border-green-300">
                <BanknotesIcon className="h-8 w-8 text-green-600 mx-auto mb-1" />
                <span className="text-sm font-medium text-green-900">Payment</span>
              </div>
            </div>
            <p className="text-center text-gray-600 mt-4 text-sm">
              Standard workflow: Create quotation from job card → Get approval → Invoice auto-generated → Record payments
            </p>
          </div>
        </div>

        {/* Main Content Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-8 overflow-hidden">
          <div className="border-b border-gray-200 overflow-x-auto">
            <nav className="flex -mb-px min-w-max">
              {[
                { id: 'quotations', name: 'Quotations', icon: DocumentTextIcon },
                { id: 'invoices', name: 'Invoices', icon: ClipboardDocumentListIcon },
                { id: 'payments', name: 'Payments', icon: CreditCardIcon },
                { id: 'receivables', name: 'Receivables', icon: BanknotesIcon },
                { id: 'payables', name: 'Payables', icon: CalculatorIcon },
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`${
                      activeTab === tab.id
                        ? 'border-yellow-500 text-yellow-600 bg-yellow-50'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } flex-1 whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm flex items-center justify-center transition-colors min-w-[140px]`}
                  >
                    <Icon className="h-5 w-5 mr-2" />
                    {tab.name}
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="p-6">
            {/* Quotations Tab */}
            {activeTab === 'quotations' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <DocumentTextIcon className="h-6 w-6 text-blue-600 mr-2" />
                    Creating a Quotation
                  </h3>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-blue-50 rounded-lg p-5 border border-blue-200">
                      <h4 className="font-semibold text-blue-900 mb-3">Step-by-Step Process</h4>
                      <ol className="space-y-3">
                        <li className="flex items-start">
                          <span className="flex-shrink-0 bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3">1</span>
                          <div>
                            <p className="font-medium text-gray-900">Navigate to Finance → Quotations</p>
                            <p className="text-sm text-gray-600">Click "Create Quotation" button</p>
                          </div>
                        </li>
                        <li className="flex items-start">
                          <span className="flex-shrink-0 bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3">2</span>
                          <div>
                            <p className="font-medium text-gray-900">Select Job Card</p>
                            <p className="text-sm text-gray-600">Customer and vehicle details auto-populate</p>
                          </div>
                        </li>
                        <li className="flex items-start">
                          <span className="flex-shrink-0 bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3">3</span>
                          <div>
                            <p className="font-medium text-gray-900">Add Line Items</p>
                            <p className="text-sm text-gray-600">Parts, services, or sublet items with quantities and prices</p>
                          </div>
                        </li>
                        <li className="flex items-start">
                          <span className="flex-shrink-0 bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3">4</span>
                          <div>
                            <p className="font-medium text-gray-900">Configure Tax (Optional)</p>
                            <p className="text-sm text-gray-600">Enable 7.5% VAT - all items or services only</p>
                          </div>
                        </li>
                        <li className="flex items-start">
                          <span className="flex-shrink-0 bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3">5</span>
                          <div>
                            <p className="font-medium text-gray-900">Save and Print</p>
                            <p className="text-sm text-gray-600">PDF generated with company letterhead</p>
                          </div>
                        </li>
                      </ol>
                    </div>

                    <div className="space-y-4">
                      <div className="bg-purple-50 rounded-lg p-5 border border-purple-200">
                        <h4 className="font-semibold text-purple-900 mb-3">Item Types</h4>
                        <div className="space-y-2">
                          <div className="flex items-center">
                            <span className="bg-purple-200 text-purple-800 px-2 py-1 rounded text-xs font-medium mr-2">PART</span>
                            <span className="text-sm text-gray-700">Physical parts and components</span>
                          </div>
                          <div className="flex items-center">
                            <span className="bg-blue-200 text-blue-800 px-2 py-1 rounded text-xs font-medium mr-2">SERVICE</span>
                            <span className="text-sm text-gray-700">Labor and service charges</span>
                          </div>
                          <div className="flex items-center">
                            <span className="bg-orange-200 text-orange-800 px-2 py-1 rounded text-xs font-medium mr-2">SUBLET</span>
                            <span className="text-sm text-gray-700">Work outsourced to third party</span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-green-50 rounded-lg p-5 border border-green-200">
                        <h4 className="font-semibold text-green-900 mb-3">Auto Margin Calculation</h4>
                        <p className="text-sm text-gray-700 mb-2">Sale prices auto-calculate based on:</p>
                        <ul className="text-sm text-gray-700 space-y-1">
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-600 mr-1 mt-0.5" />
                            <span>Service-specific margins</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-600 mr-1 mt-0.5" />
                            <span>Vehicle make/year margins</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 text-green-600 mr-1 mt-0.5" />
                            <span>Generic part margins</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                  <div className="flex">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-2 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-yellow-800">Important Notes</h4>
                      <ul className="text-sm text-yellow-700 mt-1 list-disc list-inside">
                        <li>Quotations can only be edited before approval</li>
                        <li>Approving a quotation automatically generates an invoice</li>
                        <li>All quotations include Terms & Conditions with TIN: 313422177-001 | RC: 697295</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Invoices Tab */}
            {activeTab === 'invoices' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <ClipboardDocumentListIcon className="h-6 w-6 text-purple-600 mr-2" />
                    Invoice Management
                  </h3>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-indigo-50 rounded-lg p-5 border border-indigo-200">
                      <h4 className="font-semibold text-indigo-900 mb-3">Method 1: From Quotation (Recommended)</h4>
                      <ol className="space-y-2 text-sm">
                        <li className="flex items-start">
                          <span className="bg-indigo-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2 flex-shrink-0">1</span>
                          <span className="text-gray-700">Go to Finance → Quotations</span>
                        </li>
                        <li className="flex items-start">
                          <span className="bg-indigo-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2 flex-shrink-0">2</span>
                          <span className="text-gray-700">Open quotation details</span>
                        </li>
                        <li className="flex items-start">
                          <span className="bg-indigo-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2 flex-shrink-0">3</span>
                          <span className="text-gray-700">Click "Approve Quotation and Raise Invoice"</span>
                        </li>
                        <li className="flex items-start">
                          <span className="bg-indigo-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2 flex-shrink-0">4</span>
                          <span className="text-gray-700">Invoice auto-created with unique number</span>
                        </li>
                      </ol>
                      <div className="mt-3 bg-indigo-100 rounded p-2">
                        <p className="text-xs text-indigo-800">
                          <strong>Benefits:</strong> Full audit trail, linked to job card, all details auto-populated
                        </p>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                      <h4 className="font-semibold text-gray-900 mb-3">Method 2: Manual Creation</h4>
                      <ol className="space-y-2 text-sm">
                        <li className="flex items-start">
                          <span className="bg-gray-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2 flex-shrink-0">1</span>
                          <span className="text-gray-700">Go to Finance → Invoices</span>
                        </li>
                        <li className="flex items-start">
                          <span className="bg-gray-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2 flex-shrink-0">2</span>
                          <span className="text-gray-700">Click "Create New Invoice"</span>
                        </li>
                        <li className="flex items-start">
                          <span className="bg-gray-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2 flex-shrink-0">3</span>
                          <span className="text-gray-700">Select: "For Job Card" or "For Customer"</span>
                        </li>
                        <li className="flex items-start">
                          <span className="bg-gray-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2 flex-shrink-0">4</span>
                          <span className="text-gray-700">Fill details and add items manually</span>
                        </li>
                      </ol>
                      <div className="mt-3 bg-gray-100 rounded p-2">
                        <p className="text-xs text-gray-700">
                          <strong>Use for:</strong> Supplementary invoices, standalone bills
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Invoice Status Reference</h4>
                  <div className="overflow-hidden border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Meaning</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {statusReference.invoice.map((item, idx) => {
                          const colors = getColorClasses(item.color);
                          return (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-6 py-4">
                                <span className={`px-3 py-1 inline-flex text-sm font-semibold rounded-full ${colors.badge}`}>
                                  {item.status}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-700">{item.meaning}</td>
                              <td className="px-6 py-4 text-sm text-gray-700">{item.action}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="flex items-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <PrinterIcon className="h-8 w-8 text-blue-600 mr-3" />
                  <div>
                    <h4 className="font-semibold text-blue-900">Printing Invoices</h4>
                    <p className="text-sm text-blue-700">Click "Print Invoice" to generate PDF with company letterhead, itemized breakdown, tax summary, and Terms & Conditions</p>
                  </div>
                </div>
              </div>
            )}

            {/* Payments Tab */}
            {activeTab === 'payments' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <CreditCardIcon className="h-6 w-6 text-green-600 mr-2" />
                    Recording Payments
                  </h3>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-green-50 rounded-lg p-5 border border-green-200">
                      <h4 className="font-semibold text-green-900 mb-3">How to Record a Payment</h4>
                      <ol className="space-y-3">
                        <li className="flex items-start">
                          <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3">1</span>
                          <div>
                            <p className="font-medium text-gray-900">Find the Invoice</p>
                            <p className="text-sm text-gray-600">Finance → Invoices OR Finance → Receivables</p>
                          </div>
                        </li>
                        <li className="flex items-start">
                          <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3">2</span>
                          <div>
                            <p className="font-medium text-gray-900">Click "Receive Payment"</p>
                            <p className="text-sm text-gray-600">Opens the payment modal</p>
                          </div>
                        </li>
                        <li className="flex items-start">
                          <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3">3</span>
                          <div>
                            <p className="font-medium text-gray-900">Enter Payment Details</p>
                            <p className="text-sm text-gray-600">Amount, payment mode, date</p>
                          </div>
                        </li>
                        <li className="flex items-start">
                          <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3">4</span>
                          <div>
                            <p className="font-medium text-gray-900">Submit</p>
                            <p className="text-sm text-gray-600">Balance and status auto-update</p>
                          </div>
                        </li>
                      </ol>
                    </div>

                    <div className="space-y-4">
                      <div className="bg-blue-50 rounded-lg p-5 border border-blue-200">
                        <h4 className="font-semibold text-blue-900 mb-3">Payment Modes</h4>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between bg-white p-2 rounded border">
                            <span className="text-sm font-medium text-gray-700">Bank Transfer - Stanbic Opex</span>
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Operations</span>
                          </div>
                          <div className="flex items-center justify-between bg-white p-2 rounded border">
                            <span className="text-sm font-medium text-gray-700">Bank Transfer - Stanbic Revenue</span>
                            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Revenue</span>
                          </div>
                          <div className="flex items-center justify-between bg-white p-2 rounded border">
                            <span className="text-sm font-medium text-gray-700">Bank Transfer - Stanbic CAPEX</span>
                            <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">Capital</span>
                          </div>
                          <div className="flex items-center justify-between bg-white p-2 rounded border">
                            <span className="text-sm font-medium text-gray-700">Cash</span>
                            <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">Physical</span>
                          </div>
                          <div className="flex items-center justify-between bg-white p-2 rounded border">
                            <span className="text-sm font-medium text-gray-700">POS</span>
                            <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded">Card</span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                        <h4 className="font-semibold text-red-900 mb-2 flex items-center">
                          <XCircleIcon className="h-5 w-5 mr-2" />
                          Validation Rules
                        </h4>
                        <ul className="text-sm text-red-700 space-y-1">
                          <li>• Payment date cannot be before invoice approval date</li>
                          <li>• Amount must be greater than zero</li>
                          <li>• Amount cannot exceed outstanding balance</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Receivables Tab */}
            {activeTab === 'receivables' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <BanknotesIcon className="h-6 w-6 text-blue-600 mr-2" />
                    Managing Receivables
                  </h3>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                      <div className="bg-blue-50 rounded-lg p-5 border border-blue-200 mb-4">
                        <h4 className="font-semibold text-blue-900 mb-3">What You'll See</h4>
                        <p className="text-sm text-gray-700 mb-3">
                          The receivables list shows all approved invoices with outstanding balances, automatically filtered and sorted.
                        </p>
                        <div className="overflow-hidden border border-blue-200 rounded bg-white">
                          <table className="min-w-full text-xs">
                            <thead className="bg-blue-100">
                              <tr>
                                <th className="px-3 py-2 text-left text-blue-900">Column</th>
                                <th className="px-3 py-2 text-left text-blue-900">Description</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-blue-100">
                              <tr><td className="px-3 py-2 font-medium">Invoice #</td><td className="px-3 py-2">Unique invoice number</td></tr>
                              <tr><td className="px-3 py-2 font-medium">Customer</td><td className="px-3 py-2">Customer name</td></tr>
                              <tr><td className="px-3 py-2 font-medium">Receivable</td><td className="px-3 py-2">Outstanding amount</td></tr>
                              <tr><td className="px-3 py-2 font-medium">Approval Date</td><td className="px-3 py-2">When invoice was approved</td></tr>
                              <tr><td className="px-3 py-2 font-medium">Actions</td><td className="px-3 py-2">Quick payment entry</td></tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="bg-orange-50 rounded-lg p-5 border border-orange-200">
                        <h4 className="font-semibold text-orange-900 mb-3">Aging Categories</h4>
                        <p className="text-sm text-gray-700 mb-3">
                          Track how long receivables have been outstanding:
                        </p>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between bg-white p-3 rounded border">
                            <div className="flex items-center">
                              <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                              <span className="text-sm font-medium">Current</span>
                            </div>
                            <span className="text-xs text-gray-500">0-30 days</span>
                          </div>
                          <div className="flex items-center justify-between bg-white p-3 rounded border">
                            <div className="flex items-center">
                              <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
                              <span className="text-sm font-medium">30+ Days</span>
                            </div>
                            <span className="text-xs text-gray-500">Follow up recommended</span>
                          </div>
                          <div className="flex items-center justify-between bg-white p-3 rounded border">
                            <div className="flex items-center">
                              <div className="w-3 h-3 bg-orange-500 rounded-full mr-2"></div>
                              <span className="text-sm font-medium">60+ Days</span>
                            </div>
                            <span className="text-xs text-gray-500">Priority follow up</span>
                          </div>
                          <div className="flex items-center justify-between bg-white p-3 rounded border">
                            <div className="flex items-center">
                              <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                              <span className="text-sm font-medium">90+ Days</span>
                            </div>
                            <span className="text-xs text-gray-500">Escalation required</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Payables Tab */}
            {activeTab === 'payables' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <CalculatorIcon className="h-6 w-6 text-orange-600 mr-2" />
                    Managing Payables
                  </h3>

                  <p className="text-gray-700 mb-4">
                    Payables are vendor bills from procurement orders that require payment. Track and manage all outstanding vendor obligations.
                  </p>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-orange-50 rounded-lg p-5 border border-orange-200">
                      <h4 className="font-semibold text-orange-900 mb-3">Payables List Columns</h4>
                      <div className="overflow-hidden border border-orange-200 rounded bg-white">
                        <table className="min-w-full text-xs">
                          <thead className="bg-orange-100">
                            <tr>
                              <th className="px-3 py-2 text-left text-orange-900">Column</th>
                              <th className="px-3 py-2 text-left text-orange-900">Description</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-orange-100">
                            <tr><td className="px-3 py-2 font-medium">Procurement #</td><td className="px-3 py-2">Unique procurement number</td></tr>
                            <tr><td className="px-3 py-2 font-medium">Supplier</td><td className="px-3 py-2">Vendor name</td></tr>
                            <tr><td className="px-3 py-2 font-medium">Total Cost</td><td className="px-3 py-2">Amount owed</td></tr>
                            <tr><td className="px-3 py-2 font-medium">Payment Status</td><td className="px-3 py-2">Unpaid / Partially Paid / Paid</td></tr>
                            <tr><td className="px-3 py-2 font-medium">Required Date</td><td className="px-3 py-2">When payment is due</td></tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="bg-purple-50 rounded-lg p-5 border border-purple-200">
                      <h4 className="font-semibold text-purple-900 mb-3">Recording Vendor Payments</h4>
                      <ol className="space-y-2 text-sm">
                        <li className="flex items-start">
                          <span className="bg-purple-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2">1</span>
                          <span className="text-gray-700">Find the payable in the list</span>
                        </li>
                        <li className="flex items-start">
                          <span className="bg-purple-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2">2</span>
                          <span className="text-gray-700">Click "Make Payment"</span>
                        </li>
                        <li className="flex items-start">
                          <span className="bg-purple-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2">3</span>
                          <span className="text-gray-700">Enter amount, mode, and date</span>
                        </li>
                        <li className="flex items-start">
                          <span className="bg-purple-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2">4</span>
                          <span className="text-gray-700">Click "Submit Payment"</span>
                        </li>
                      </ol>
                      <div className="mt-3 bg-purple-100 rounded p-2">
                        <p className="text-xs text-purple-800">
                          <strong>Note:</strong> Payment date cannot be before procurement date
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Payable Status Reference</h4>
                    <div className="overflow-hidden border border-gray-200 rounded-lg">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Meaning</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {statusReference.payable.map((item, idx) => {
                            const colors = getColorClasses(item.color);
                            return (
                              <tr key={idx} className="hover:bg-gray-50">
                                <td className="px-6 py-4">
                                  <span className={`px-3 py-1 inline-flex text-sm font-semibold rounded-full ${colors.badge}`}>
                                    {item.status}
                                  </span>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-700">{item.meaning}</td>
                                <td className="px-6 py-4 text-sm text-gray-700">{item.action}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <InformationCircleIcon className="h-7 w-7 text-yellow-600 mr-2" />
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            {faqs.map((category, categoryIdx) => (
              <div key={categoryIdx}>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{category.category}</h3>
                <div className="space-y-2">
                  {category.questions.map((faq, faqIdx) => {
                    const globalIdx = `${categoryIdx}-${faqIdx}`;
                    const isOpen = openFaq === globalIdx;
                    return (
                      <div key={faqIdx} className="border border-gray-200 rounded-lg overflow-hidden">
                        <button
                          onClick={() => toggleFaq(globalIdx)}
                          className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
                        >
                          <span className="font-medium text-gray-900 pr-4">{faq.q}</span>
                          {isOpen ? (
                            <ChevronUpIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
                          ) : (
                            <ChevronDownIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
                          )}
                        </button>
                        {isOpen && (
                          <div className="p-4 bg-white border-t border-gray-200">
                            <p className="text-gray-700 whitespace-pre-line">{faq.a}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Daily Checklist */}
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg shadow-md p-6 mb-8 border-2 border-green-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Daily Finance Checklist</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-4 shadow">
              <h3 className="font-semibold text-blue-900 mb-3 flex items-center">
                <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded mr-2">AM</span>
                Start of Day
              </h3>
              <ul className="text-sm text-gray-700 space-y-2">
                <li className="flex items-start">
                  <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Review pending quotations</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Check receivables aging report</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Review urgent payables</span>
                </li>
              </ul>
            </div>
            <div className="bg-white rounded-lg p-4 shadow">
              <h3 className="font-semibold text-yellow-900 mb-3 flex items-center">
                <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded mr-2">ONGOING</span>
                Throughout Day
              </h3>
              <ul className="text-sm text-gray-700 space-y-2">
                <li className="flex items-start">
                  <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Record payments as received</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Process quotation approvals</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Follow up on aged receivables</span>
                </li>
              </ul>
            </div>
            <div className="bg-white rounded-lg p-4 shadow">
              <h3 className="font-semibold text-purple-900 mb-3 flex items-center">
                <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded mr-2">PM</span>
                End of Day
              </h3>
              <ul className="text-sm text-gray-700 space-y-2">
                <li className="flex items-start">
                  <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Verify all payments recorded</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Check outstanding payables</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Review tomorrow's due items</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Company Info */}
        <div className="bg-gray-100 rounded-lg p-6 mb-8">
          <h3 className="font-semibold text-gray-900 mb-2">Company Details for Documents</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
            <div>
              <p><strong>Company:</strong> AYDENS Garage</p>
              <p><strong>Address:</strong> Block 7, Plot 7, Etal Avenue, Off Kudirat Abiola Way, Oregun, Lagos, Nigeria</p>
            </div>
            <div>
              <p><strong>Phone:</strong> +234-7045972414</p>
              <p><strong>Email:</strong> info@aydensgarage.com</p>
              <p><strong>TIN:</strong> 313422177-001 | <strong>RC:</strong> 697295</p>
            </div>
          </div>
        </div>

        {/* Footer Navigation */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <Link
            to="/help"
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            ← Back to Help Center
          </Link>
          <div className="flex gap-3">
            <Link
              to="/finance/quotations"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              Go to Quotations
            </Link>
            <Link
              to="/finance/invoices"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700"
            >
              Go to Invoices
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default AccountsGuide;
